/**
 * feat-107 shared primitive (4a/4) — canonical Notion import template (ADR 0022).
 *
 * SINGLE SOURCE OF TRUTH for the paste grammar. Used verbatim by BOTH:
 *   1. the "Copy template for Notion" button on importer Step 1 (Slice B), and
 *   2. the parser's golden unit test (Slice B).
 * Keeping one string means the format can never drift between what the button
 * hands the user and what the parser is proven to accept.
 *
 * ORIGIN: this content mirrors the human-authored canonical doc at
 *   .team/features/feat-104-curriculum-editor-redesign/notion-import-template.txt
 * It is vendored here as a TS module because that `.team/` path is OUTSIDE the
 * interviewer-frontend git repo and outside `src/`, so webpack (Copy button) and
 * jest (golden test) cannot import it directly. If the grammar changes, update
 * the `.team` doc AND this constant together; both in-repo consumers follow.
 *
 * `Role:` — feat-248. THE VALUE MUST MATCH A ROLE CONFIGURED FOR THE TENANT,
 * specifically one with an ACTIVE PROMPT TEMPLATE VERSION. It is not free text
 * and it is not a label: `create_rehearsal` resolves it to a prompt template
 * version by EXACT string match and hard-400s when there is none, because that
 * version supplies the grading domain context and the transcription language.
 * `Front-counter greeter` below is illustrative of the FORM, not a role any
 * tenant necessarily has — the importer's review step now resolves the pasted
 * value against the tenant's real roles and offers a dropdown, so a paste can
 * no longer reach commit carrying a role that will fail.
 *
 * GOLDEN CONTRACT (asserted by the parser test): parsing this string yields
 *   2 Drills · 2 MC + 2 Fill questions · 1 Rehearsal · 1 Reading (feat-343,
 *   with a heading, bold, a bullet list and its closing `---`) · ZERO
 *   Needs-review flags,
 *   with Praise/Explain on every question, A–D options + Correct on the
 *   fill-blanks, non-empty label + description on every rehearsal criterion
 *   (feat-109), and a non-empty `correct_example_response` on the rehearsal
 *   (feat-187 — the `Correct example:` line; without it the import ships a
 *   rehearsal whose "Try saying" card is blank, which is now a warn flag).
 */

export const NOTION_IMPORT_TEMPLATE = `## Drill Name Goes Here

1. Write a multiple-choice question here. What is the best answer?
A) A plausible wrong answer
B) The correct answer
C) Another wrong answer
D) One more wrong answer
Correct: B
Praise: Exactly — that's the answer we're looking for.
Explain: Not quite — re-read the question and the answer becomes clear.

2. Write a fill-in-the-blank question here — a great greeting always sounds ___ , never pushy.
A) warm
B) robotic
C) rushed
D) loud
Correct: A
Accept: friendly, genuine
Praise: Yes — warm is exactly the tone we want.
Explain: Aim for a warm, genuine tone rather than a rushed or pushy one.

Rehearsal: Short name for this spoken-practice scenario
Role: Front-counter greeter
Scenario: Describe the situation the trainee should role-play. Be specific about the guest, the moment, and the goal.
Correct example: Hi, welcome in! Have you eaten with us before? I can walk you through the menu.
Good things:
- Warm greeting: Greets the guest within a few seconds with a genuine smile.
- Offers help: Asks what the guest is looking for and points them to a good option.
Bad things:
- Ignores the guest: Lets the guest stand at the counter without acknowledgement.
- Pushy upsell: Pressures the guest into add-ons they did not ask about.

## Second Drill (add as many Drill sections as you like)

Reading: Short title for a reading
Everything after the Reading line is the reading text, until a --- divider line. Formatting is kept: **bold**, *italic* and [links](https://example.com).

### A small heading
- A bullet point
- Another bullet point
---

3. Another multiple-choice question?
A) Wrong
B) Wrong
C) Right
D) Wrong
Correct: C
Praise: Right on — C is the correct answer.
Explain: Take another look — only one option fully fits.

4. Another fill-blank — the combo saves the guest ___ compared to buying separately.
A) money
B) time
C) calories
D) nothing
Correct: A
Accept: $2, two dollars
Praise: Correct — the combo saves the guest money.
Explain: Think about the dollar value the combo adds up against buying each item.
`;
